/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * The shop's one rule for drawing money: the currency symbol first, then the
 * digits grouped for the shop's active language -- "$29.64" / "$ 29.64" in
 * English, "$29,64" / "$ 29,64" in German. Dollars are drawn this way in every
 * language (VS-3 and N-2, S803 review); only other currencies keep their own
 * country's layout, through Intl, where they are drawn at all.
 *
 * Depends only on the currency table and the active locale, so anything in the
 * shop can import it without a cycle.
 */

import { formatNumber } from "@/lib/active-locale";
import { getCurrencySymbol, isZeroDecimal } from "@/lib/currencies";

/**
 * NF-1: a zero-decimal currency is already in whole units, so dividing it by one
 * hundred understates it by a factor of one hundred. That figure reached the
 * refund dialog, which sends what it was shown to the server, so a 2,400 yen
 * order was refunded as 24 yen with no error anywhere. Zero-decimal amounts are
 * also large, so they are rendered with thousands separators and no decimals.
 */
const renderAmount = (currency: string, amount: number, separator: string) => {
  const symbol = getCurrencySymbol(currency.toUpperCase());
  // N-10 (S658): the DIGITS follow the shop's active language, exactly as every
  // other figure on the same screen already does. This was the last fixed
  // "en-US" left in the money path, and it is the one that mattered most,
  // because `formatCurrency` draws the amount on the billing table, the refund
  // dialog, the cart summary and the Thank You page. A German customer read
  // "1,234.56" here beside "1.234,56" from the currency service on the same
  // page -- and "1,234.56" in German means one and a bit, not twelve hundred.
  // Only the grouping and the decimal mark move. The symbol stays in front of
  // the amount, which is where this shop has always drawn it and what every
  // caller's column width is measured against; symbol PLACEMENT per locale is a
  // separate, larger change and is deliberately not made here.
  if (isZeroDecimal(currency)) {
    return `${symbol}${separator}${formatNumber(Math.round(amount))}`;
  }
  return `${symbol}${separator}${formatNumber(amount / 100, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatCurrency = (currency: string, amount: number) =>
  renderAmount(currency, amount, "");

/** Differs from formatCurrency only by a space after the symbol. */
export const formatCurrencyCart = (currency: string, amount: number) =>
  renderAmount(currency, amount, " ");
